import type { Form } from "@/types/form";
import type { Tag } from "@/types/tag";
import type { Lifecycle } from "@/types/lifecycle";
import type { Segment } from "@/types/segment";
import type { Website } from "@/types/website";

import { getMockMarketProfile } from "../market";

import { createId, createPastTimestamp, randomInt } from "./base";

import { VISIT_HISTORY_OPTIONS } from "@/features/contact/constants/visit-history";

const MARKET = getMockMarketProfile();

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const FORM_SEEDS: Array<Pick<Form, "name" | "source">> = [
  { name: "Free Consultation Request", source: "website" },
  { name: "Meta Lead Ad - Free Consultation", source: "meta" },
  { name: "TikTok Lead Generation Form", source: "tiktok" },
  { name: "Snapchat Lead Form", source: "snapchat" },
  { name: "Newsletter Signup", source: "website" },
];

export function generateForms(): Form[] {
  return FORM_SEEDS.map((seed) => ({
    id: createId(),
    externalId: null,
    name: seed.name,
    source: seed.source,
    url:
      seed.source === "website" ? `${MARKET.organizationWebsite}/book` : null,
    createdAt: createPastTimestamp(60),
    submissionCount: randomInt(5, 120),
  }));
}

const WEBSITE_SEEDS: Array<Pick<Website, "name" | "domain">> = [
  {
    name: MARKET.organizationName,
    domain: new URL(MARKET.organizationWebsite).hostname,
  },
  { name: "Glow Med Spa", domain: "glowmedspa.com" },
];

export function generateWebsites(
  organizationId: string,
  workspaceId: string,
): Website[] {
  return WEBSITE_SEEDS.map((seed) => ({
    id: createId(),
    domain: seed.domain,
    name: seed.name,
    organizationId,
    workspaceId,
    createdAt: createPastTimestamp(120),
    updatedAt: createPastTimestamp(10),
  }));
}

type SystemSegmentSeed = Pick<Segment, "name" | "slug" | "filter">;

const NEW_THIS_WEEK: SystemSegmentSeed = {
  name: "New this week",
  slug: "system:new-this-week",
  filter: {
    rules: [
      {
        field: "createdAt",
        operator: "inTheLast",
        value: { amount: 7, unit: "days" },
      },
    ],
  },
};

// Mirrors the backend's built-in segments, in its order; the mock matcher reads their rules as indeterminate.
const SYSTEM_SEGMENT_SEEDS: Record<"clinic" | "store", SystemSegmentSeed[]> = {
  clinic: [
    ...VISIT_HISTORY_OPTIONS.map(
      ({ value, label }): SystemSegmentSeed => ({
        name: label,
        slug: `system:${value.replaceAll("_", "-")}`,
        filter: {
          rules: [
            { field: "visitHistory", operator: "hasAnyOf", value: [value] },
          ],
        },
      }),
    ),
  ],
  store: [
    NEW_THIS_WEEK,
    {
      name: "Never ordered",
      slug: "system:never-ordered",
      filter: {
        rules: [{ field: "order", operator: "doesNotExist", value: "" }],
      },
    },
    {
      name: "Ordered",
      slug: "system:ordered",
      filter: { rules: [{ field: "order", operator: "exists", value: "" }] },
    },
  ],
};

const systemSegments = (isClinic: boolean): Segment[] =>
  SYSTEM_SEGMENT_SEEDS[isClinic ? "clinic" : "store"].map((seed) => ({
    ...seed,
    id: createId(),
    contactCount: randomInt(3, 40),
    editable: false,
  }));

export function generateSegments(
  tags: Tag[],
  lifecycles: Lifecycle[],
  isClinic: boolean,
): Segment[] {
  const seeds: Pick<Segment, "name" | "filter">[] = [
    {
      name: "VIP Patients",
      filter: {
        rules: [
          {
            field: "tags",
            operator: "isEqualTo",
            value: tags.find((tag) => tag.name === "VIP Patient")?.id,
          },
        ],
      },
    },
    {
      name: "New Leads",
      filter: {
        rules: [
          {
            field: "lifecycle",
            operator: "isEqualTo",
            value: lifecycles[0]?.id,
          },
        ],
      },
    },
    {
      name: "Email available",
      filter: { rules: [{ field: "email", operator: "notNull", value: "" }] },
    },
    {
      name: "Needs Follow-up",
      filter: {
        rules: [
          { field: "followUpRequired", operator: "isEqualTo", value: true },
        ],
      },
    },
  ];

  return [
    ...systemSegments(isClinic),
    ...seeds.map(({ name, filter }) => ({
      id: createId(),
      name,
      slug: slugify(name),
      filter,
      contactCount: randomInt(3, 40),
      editable: true,
    })),
  ];
}
